// HomeClient.jsx
import Table from "react-bootstrap/Table";
import { useState, useEffect, useRef, useCallback } from "react";
import CheckBoxPay from "./CheckBoxPay";
import { generateReceiptPDF } from "./ReceiptPDF";

const HomeClient = ({ BaseUrl }) => {
  const dataSkeleteon = [1, 2, 3, 4, 5, 7, 8];
  const user_id = localStorage.getItem("user_id");

  const LIMIT = 100;

  const [orders, setOrders] = useState([]);
  const [debt, setDebt] = useState([]);
  const [loading, setLoading] = useState(false); // initial loading
  const [loadingMore, setLoadingMore] = useState(false); // pagination loading
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef(null);
  const inFlightRef = useRef(false);
  const initialLoadedRef = useRef(false);

  // Функция для генерации чека
  const handleReceiptClick = (e, order) => {
    e.preventDefault();
    generateReceiptPDF(order);
  };

  const fetchPage = useCallback(
    async ({ nextOffset, isInitial }) => {
      if (inFlightRef.current) return;
      if (!isInitial && !hasMore) return;

      inFlightRef.current = true;

      const controller = new AbortController();
      const signal = controller.signal;

      try {
        if (isInitial) setLoading(true);
        else setLoadingMore(true);

        const url = isInitial
          ? `${BaseUrl}api/users/get_client/${user_id}` // 👈 как требуется: дефолтные 100
          : `${BaseUrl}api/users/get_client/${user_id}?limit=${LIMIT}&offset=${nextOffset}`;

        const response = await fetch(url, { signal });
        const actualData = await response.json();

        // debt всегда общий — обновляем на каждом ответе
        setDebt(actualData["debt"]);

        const newOrders = actualData["orders"] || [];

        setOrders((prev) => (isInitial ? newOrders : [...prev, ...newOrders]));

        const pg = actualData["orders_pagination"];
        if (pg && typeof pg.has_more === "boolean") {
          setHasMore(pg.has_more);
        } else {
          // если pagination не пришла — считаем что больше нет, когда пришло меньше лимита
          setHasMore(newOrders.length === LIMIT);
        }

        if (isInitial) {
          setOffset(newOrders.length); // дефолтный запрос отдаёт 100
        } else {
          setOffset(nextOffset + newOrders.length);
        }
      } catch (e) {
        // ничего не ломаем: просто молча игнорируем отменённые/ошибочные запросы
      } finally {
        if (isInitial) setLoading(false);
        else setLoadingMore(false);

        inFlightRef.current = false;

        // если размонтировали — отмена будет через AbortController,
        // но тут мы не храним controller между вызовами, поэтому без cleanup.
        // (не влияет на работоспособность)
      }
    },
    [BaseUrl, user_id, hasMore],
  );

  useEffect(() => {
    // initial load (один раз)
    if (initialLoadedRef.current) return;
    initialLoadedRef.current = true;

    fetchPage({ nextOffset: 0, isInitial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // infinite scroll через IntersectionObserver
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        // не подгружаем пока идёт initial load
        if (loading) return;

        // если есть ещё — грузим следующую страницу
        if (hasMore && !inFlightRef.current) {
          fetchPage({ nextOffset: offset, isInitial: false });
        }
      },
      {
        root: null,
        rootMargin: "200px", // заранее подгружаем до низа
        threshold: 0,
      },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [fetchPage, hasMore, offset, loading]);

  return (
    <div className="container rounded shadow mt-3 white pt-3 pb-3 text-center">
      <div className="row ps-4 pt-2">
        <div className="col-12 text-start mt-4 ps-3">
          <h3 style={{ fontWeight: 700, color: "#152536" }}>Мои заказы</h3>
          {loading ? (
            <span className="placeholder col-2 placeholder-lg"></span>
          ) : (
            <p style={{ color: "red", fontWeight: "bold" }}>Мой долг: {debt}</p>
          )}
        </div>

        <div className="col-12 placeholder-glow mt-3">
          <Table hover responsive="xl">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Номер заказа</th>
                <th>Магазин</th>
                <th>Название</th>
                <th>Ссылка(код)</th>
                <th>Кол-во</th>
                <th>Итого</th>
                <th>Вес</th>
                <th>Статус</th>
                <th>Оплачен</th>
              </tr>
            </thead>

            <tbody>
              {loading
                ? dataSkeleteon.map((index) => (
                    <tr key={index}>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                      <td>
                        <span className="placeholder col-5 placeholder-lg"></span>
                      </td>
                    </tr>
                  ))
                : orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        {(order.date || "").split("-")[2]}.{(order.date || "").split("-")[1]}.
                        {(order.date || "").split("-")[0]}
                      </td>
                      <td>
                        {order.number} (
                        <a
                          href="#"
                          onClick={(e) => handleReceiptClick(e, order)}
                          style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                        >
                          Чек
                        </a>
                        )
                      </td>
                      <td>{order.shop}</td>
                      <td>{order.name}</td>
                      <td>
                        <a
                          href={order.link}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "black" }}
                        >
                          {order.sku}
                        </a>
                      </td>
                      <td>{order.quantity}</td>
                      <td style={{ fontWeight: 700 }}>{order.total}₽</td>
                      <td>
                        {order.weight !== null && order.weight !== 0 ? `${order.weight}кг` : ""}
                      </td>
                      <td>{order.status?.text}</td>
                      <td>
                        <CheckBoxPay pay={order.pay} />
                      </td>
                    </tr>
                  ))}
            </tbody>
          </Table>

          {/* Sentinel для infinite scroll */}
          <div ref={sentinelRef} style={{ height: 1 }} />

          {/* Индикатор догрузки */}
          {loadingMore ? (
            <div className="mt-3">
              <span className="placeholder col-2 placeholder-lg"></span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default HomeClient;
